import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CurrencyInput, formatCurrencyValue, roundMoney } from '@/components/ui/currency-input';
import { format, subDays, addDays } from 'date-fns';
import { pt } from 'date-fns/locale';
import { 
  Store, Calendar, Banknote, CreditCard, Calculator, 
  CheckCircle2, AlertTriangle, XCircle, Info, RefreshCw,
  ChevronLeft, ChevronRight, Save, Lock, Unlock, FileText,
  TrendingUp, TrendingDown, Minus, Clock, Receipt
} from 'lucide-react';
import { StorePeriodFilter, useStorePeriodFilter, isSingleDayPeriod } from '@/components/retail/StorePeriodFilter';
import { useStoreMapping } from '@/hooks/useStoreMapping';

// Format currency - use the safe version
const formatCurrency = formatCurrencyValue;

// Status badge component
const StatusBadge = ({ status, statusDisplay }) => {
  const colorMap = {
    gray: 'bg-slate-100 text-slate-600 border-slate-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    yellow: 'bg-amber-50 text-amber-700 border-amber-200',
    green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    red: 'bg-red-50 text-red-700 border-red-200',
  };
  
  const iconMap = {
    NO_DATA: Info,
    OPEN: Clock,
    READY_TO_CLOSE: CheckCircle2,
    CLOSED_OK: CheckCircle2,
    CLOSED_WITH_DIFFERENCE: AlertTriangle,
    REOPENED: Unlock,
    POST_CLOSE_DATA_CHANGE: XCircle,
  };
  
  const Icon = iconMap[status] || Info;
  const color = statusDisplay?.color || 'gray';
  
  return (
    <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${colorMap[color]}`}
         data-testid={`status-badge-${status}`}>
      <Icon className="w-4 h-4" />
      <span>{statusDisplay?.label || status}</span>
    </div>
  );
};

// Difference display component
const DifferenceDisplay = ({ value, label }) => {
  if (value === null || value === undefined) {
    return <span className="text-slate-400">—</span>;
  }
  
  const isZero = Math.abs(value) < 0.01;
  const isPositive = value > 0;
  const isNegative = value < 0;
  
  return (
    <div className={`flex items-center gap-1 ${isZero ? 'text-emerald-600' : isNegative ? 'text-red-600' : 'text-amber-600'}`}>
      {isZero ? (
        <CheckCircle2 className="w-4 h-4" />
      ) : isPositive ? (
        <TrendingUp className="w-4 h-4" />
      ) : (
        <TrendingDown className="w-4 h-4" />
      )}
      <span className="font-medium">
        {isZero ? 'OK' : isPositive ? `Sobra ${formatCurrency(value)}` : `Falta ${formatCurrency(Math.abs(value))}`}
      </span>
    </div>
  );
};

// Main component
export default function RetailFechoCaixa() {
  const { getStoreName } = useStoreMapping();
  const { user } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Use shared filter hook - default to 'this_month' for all retail pages
  const {
    selectedStore,
    setSelectedStore,
    period,
    setPeriod,
    customStartDate,
    setCustomStartDate,
    customEndDate,
    setCustomEndDate,
    startDate,
    endDate,
    isSingleDay,
    filterProps,
  } = useStorePeriodFilter({ defaultPeriod: 'this_month', defaultStore: '' });
  
  // State
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Closure data (for single day) or closures list (for range)
  const [closure, setClosure] = useState(null);
  const [closures, setClosures] = useState([]);
  const [countedCash, setCountedCash] = useState(null);
  const [tpaClosing, setTpaClosing] = useState(null);
  const [notes, setNotes] = useState('');
  
  // Reopen state
  const [showReopen, setShowReopen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  
  // Auto-select store if user has only one
  useEffect(() => {
    if (!selectedStore && user?.allowed_warehouses?.length === 1) {
      setSelectedStore(user.allowed_warehouses[0]);
    }
  }, [user, selectedStore, setSelectedStore]);
  
  // Load closure data
  useEffect(() => {
    if (!selectedStore) return;
    
    const loadClosure = async () => {
      setLoading(true);
      setError(null);
      
      try {
        if (isSingleDay) {
          // Single day - load individual closure
          const res = await axios.get(`${API}/retail-closures/closures/${selectedStore}/${startDate}`);
          setClosure(res.data);
          setClosures([]);
          
          // Populate form with existing values
          if (res.data.declared?.counted_cash !== null) {
            setCountedCash(roundMoney(res.data.declared.counted_cash));
          } else {
            setCountedCash(null);
          }
          if (res.data.declared?.tpa_closing_amount !== null) {
            setTpaClosing(roundMoney(res.data.declared.tpa_closing_amount));
          } else {
            setTpaClosing(null);
          }
          setNotes(res.data.notes || '');
        } else {
          // Date range - load list of closures
          const params = new URLSearchParams();
          params.set('store_code', selectedStore);
          params.set('start_date', startDate);
          params.set('end_date', endDate);
          
          const res = await axios.get(`${API}/retail-closures/closures-range?${params.toString()}`);
          setClosures(res.data || []);
          setClosure(null);
          setCountedCash(null);
          setTpaClosing(null);
          setNotes('');
        }
      } catch (err) {
        console.error('Error loading closure:', err);
        setError(err.response?.data?.detail || 'Erro ao carregar fecho');
        setClosure(null);
        setClosures([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadClosure();
  }, [selectedStore, startDate, endDate, isSingleDay]);
  
  // Calculate differences in real-time
  const calculations = useMemo(() => {
    if (!closure) return null;
    
    const rc = closure.payment_reclassification;
    const wintouchCash = roundMoney(rc ? rc.wintouch_cash : (closure.expected?.cash || 0));
    const wintouchCard = roundMoney(rc ? rc.wintouch_card : (closure.expected?.card || 0));
    const cashFloat = roundMoney(closure.cash_float_retained || 0);
    
    const countedCashNum = countedCash !== null ? roundMoney(countedCash) : null;
    const tpaClosingNum = tpaClosing !== null ? roundMoney(tpaClosing) : null;
    
    // Same rule as backend: declared TOTAL equals WinTouch to the cent but split differs
    // -> payment method mis-registered at the sale; the user's split becomes the expected one
    let expectedCash = wintouchCash;
    let expectedCard = wintouchCard;
    let reclassified = false;
    if (countedCashNum !== null && tpaClosingNum !== null) {
      const declaredCash = roundMoney(countedCashNum - cashFloat);
      const splitDiffers = roundMoney(declaredCash - wintouchCash) !== 0 || roundMoney(tpaClosingNum - wintouchCard) !== 0;
      if (splitDiffers && declaredCash >= 0 && roundMoney(declaredCash + tpaClosingNum) === roundMoney(wintouchCash + wintouchCard)) {
        expectedCash = declaredCash;
        expectedCard = tpaClosingNum;
        reclassified = true;
      }
    }
    
    // CORRECT FORMULA: Cash difference must account for cash float (fundo de caixa)
    // The operator counts ALL physical cash, which includes the float that stays in register
    // Expected in register = Cash Float + Expected Cash from Sales
    // Cash Difference = Counted Cash - Expected in Register
    const expectedInRegister = roundMoney(cashFloat + expectedCash);
    const cashDiff = countedCashNum !== null ? roundMoney(countedCashNum - expectedInRegister) : null;
    const cardDiff = tpaClosingNum !== null ? roundMoney(tpaClosingNum - expectedCard) : null;
    const depositable = countedCashNum !== null ? roundMoney(Math.max(0, countedCashNum - cashFloat)) : null;
    
    return {
      expectedCash,
      expectedCard,
      wintouchCash,
      wintouchCard,
      reclassified,
      movedToCard: roundMoney(wintouchCash - expectedCash),
      cashFloat,
      expectedInRegister,
      countedCash: countedCashNum,
      tpaClosing: tpaClosingNum,
      cashDiff,
      cardDiff,
      depositable,
    };
  }, [closure, countedCash, tpaClosing]);
  
  // Save values (without confirming)
  const handleSave = async () => {
    if (!selectedStore || !startDate || !isSingleDay) return;
    
    setSaving(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (countedCash !== null) params.set('counted_cash', roundMoney(countedCash).toString());
      if (tpaClosing !== null) params.set('tpa_closing_amount', roundMoney(tpaClosing).toString());
      if (notes) params.set('notes', notes);
      
      const res = await axios.put(
        `${API}/retail-closures/closures/${selectedStore}/${startDate}?${params.toString()}`
      );
      setClosure(res.data);
    } catch (err) {
      console.error('Error saving closure:', err);
      setError(err.response?.data?.detail || 'Erro ao guardar');
    } finally {
      setSaving(false);
    }
  };
  
  // Confirm closure
  const handleConfirm = async () => {
    if (!selectedStore || !startDate || !isSingleDay) return;
    
    setSaving(true);
    setError(null);
    
    try {
      // First save current values
      await handleSave();
      
      // Then confirm
      const res = await axios.post(
        `${API}/retail-closures/closures/${selectedStore}/${startDate}/confirm`,
        { notes }
      );
      setClosure(res.data);
    } catch (err) {
      console.error('Error confirming closure:', err);
      setError(err.response?.data?.detail || 'Erro ao confirmar fecho');
    } finally {
      setSaving(false);
    }
  };
  
  // Reopen closure
  const handleReopen = async () => {
    if (!selectedStore || !startDate || !reopenReason.trim() || !isSingleDay) return;
    
    setSaving(true);
    setError(null);
    
    try {
      const res = await axios.post(
        `${API}/retail-closures/closures/${selectedStore}/${startDate}/reopen`,
        { reason: reopenReason }
      );
      setClosure(res.data);
      setShowReopen(false);
      setReopenReason('');
    } catch (err) {
      console.error('Error reopening closure:', err);
      setError(err.response?.data?.detail || 'Erro ao reabrir fecho');
    } finally {
      setSaving(false);
    }
  };
  
  // Navigate dates (for single day mode)
  const goToPreviousDay = () => {
    const prev = subDays(new Date(startDate), 1);
    setPeriod('custom');
    const newDate = format(prev, 'yyyy-MM-dd');
    setCustomStartDate(newDate);
    setCustomEndDate(newDate);
  };
  
  const goToNextDay = () => {
    const next = addDays(new Date(startDate), 1);
    setPeriod('custom');
    const newDate = format(next, 'yyyy-MM-dd');
    setCustomStartDate(newDate);
    setCustomEndDate(newDate);
  };
  
  const goToToday = () => {
    setPeriod('today');
  };
  
  const handleRefresh = () => {
    setLoading(true);
    window.location.reload();
  };
  
  // Check if closure is editable
  const isEditable = closure && !['CLOSED_OK', 'CLOSED_WITH_DIFFERENCE', 'POST_CLOSE_DATA_CHANGE'].includes(closure.status);
  const isConfirmed = closure && ['CLOSED_OK', 'CLOSED_WITH_DIFFERENCE', 'POST_CLOSE_DATA_CHANGE'].includes(closure.status);
  const canConfirm = closure && closure.status === 'READY_TO_CLOSE';
  const canReopen = isConfirmed && user?.role && ['admin', 'procurement_manager'].includes(user.role);
  
  return (
    <div className="min-h-screen bg-slate-50 pb-24 md:pb-8" data-testid="fecho-caixa-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-4 md:py-6">
          <div className="flex flex-col gap-4">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Receipt className="h-5 w-5 md:h-6 md:w-6 text-red-600" />
                Fecho de Caixa
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Reconciliação diária de numerário e multibanco
              </p>
            </div>
            
            {/* Shared Store/Period Filter */}
            <div className="flex flex-wrap items-center gap-3">
              <StorePeriodFilter
                {...filterProps}
                onRefresh={handleRefresh}
                loading={loading}
                showAllStores={false}
              />
              
              {/* Quick day navigation for single-day mode */}
              {isSingleDay && (
                <div className="flex items-center gap-1 ml-2">
                  <Button variant="outline" size="icon" onClick={goToPreviousDay} data-testid="prev-day">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="icon" onClick={goToNextDay} data-testid="next-day">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={goToToday} className="ml-1">
                    Hoje
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-4 md:py-6 space-y-6">
        {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Erro</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-slate-400 animate-spin" />
        </div>
      )}
      
      {/* No store selected */}
      {!loading && !selectedStore && (
        <Card>
          <CardContent className="py-12 text-center">
            <Store className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700">Selecione uma loja</h3>
            <p className="text-sm text-slate-500 mt-1">
              Escolha a loja para efetuar o fecho de caixa
            </p>
          </CardContent>
        </Card>
      )}
      
      {/* Multi-day range view */}
      {!loading && selectedStore && !isSingleDay && (
        <Card>
          <CardHeader>
            <CardTitle>Resumo de Fechos</CardTitle>
            <CardDescription>
              {startDate} a {endDate}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {closures.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8">
                Sem dados para o período selecionado
              </p>
            ) : (
              <div className="space-y-3">
                {closures.map((c) => (
                  <div 
                    key={c.business_date}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100"
                    onClick={() => {
                      setPeriod('custom');
                      setCustomStartDate(c.business_date);
                      setCustomEndDate(c.business_date);
                    }}
                  >
                    <div>
                      <span className="font-medium">{format(new Date(c.business_date), 'dd/MM/yyyy', { locale: pt })}</span>
                      <StatusBadge status={c.status} statusDisplay={c.status_display} />
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">
                        Vendas: {formatCurrency(c.expected?.net_sales)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Single day closure form */}
      {!loading && closure && isSingleDay && (
        <div className="space-y-6">
          {/* Status and summary */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">{getStoreName(selectedStore)}</CardTitle>
                  <CardDescription>
                    {format(new Date(startDate), "EEEE, d 'de' MMMM 'de' yyyy", { locale: pt })}
                  </CardDescription>
                </div>
                <StatusBadge status={closure.status} statusDisplay={closure.status_display} />
              </div>
            </CardHeader>
            
            {closure.status === 'NO_DATA' && (
              <CardContent>
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertTitle>Sem dados WinTouch</AlertTitle>
                  <AlertDescription>
                    Não existem dados de vendas para este dia. Importe ficheiros XML primeiro.
                  </AlertDescription>
                </Alert>
              </CardContent>
            )}
            
            {closure.status !== 'NO_DATA' && (
              <CardContent className="space-y-6">
                {/* Expected values summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Vendas Líquidas</p>
                    <p className="text-lg font-bold text-slate-900">{formatCurrency(closure.expected?.net_sales)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Numerário Esperado</p>
                    <p className="text-lg font-bold text-emerald-600" data-testid="closure-expected-cash">{formatCurrency(calculations?.expectedCash)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Multibanco Esperado</p>
                    <p className="text-lg font-bold text-blue-600" data-testid="closure-expected-card">{formatCurrency(calculations?.expectedCard)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Fundo de Caixa</p>
                    <p className="text-lg font-bold text-slate-600">{formatCurrency(closure.cash_float_retained)}</p>
                  </div>
                </div>
                
                {calculations?.reclassified && (
                  <div className="flex gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="closure-payment-reclassified">
                    <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-600" />
                    <div>
                      <p className="font-medium">Forma de pagamento corrigida — total certo ao cêntimo</p>
                      <p className="text-emerald-800">
                        {formatCurrency(Math.abs(calculations.movedToCard))} {calculations.movedToCard > 0 ? 'registados em numerário no WinTouch foram pagos por Multibanco' : 'registados em Multibanco no WinTouch foram pagos em numerário'}.
                        {' '}Ficam como corretos os valores indicados: numerário {formatCurrency(calculations.expectedCash)} · MB {formatCurrency(calculations.expectedCard)} (WinTouch: {formatCurrency(calculations.wintouchCash)} · {formatCurrency(calculations.wintouchCard)}). Depósitos e Controlo Diário usam estes valores.
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Input form */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Cash section */}
                  <Card className="border-emerald-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Banknote className="w-5 h-5 text-emerald-600" />
                        Numerário
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Show breakdown: WinTouch + Float = Expected in Register */}
                      <div className="space-y-2 p-3 bg-emerald-50/50 rounded-lg">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-slate-500">{calculations?.reclassified ? 'Esperado (corrigido)' : 'Esperado WinTouch'}</span>
                          <span className="font-medium text-emerald-600">{formatCurrency(calculations?.expectedCash)}</span>
                        </div>
                        {calculations?.cashFloat > 0 && (
                          <>
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-slate-500">Fundo de Caixa</span>
                              <span className="font-medium text-slate-600">+ {formatCurrency(calculations?.cashFloat)}</span>
                            </div>
                            <div className="flex justify-between items-center pt-2 border-t border-emerald-200">
                              <span className="text-xs font-medium text-slate-700">Esperado em Caixa</span>
                              <span className="font-bold text-emerald-700">{formatCurrency(calculations?.expectedInRegister)}</span>
                            </div>
                          </>
                        )}
                      </div>
                      
                      <div>
                        <Label htmlFor="counted-cash">Numerário Contado</Label>
                        <CurrencyInput
                          id="counted-cash"
                          value={countedCash}
                          onChange={setCountedCash}
                          allowNegative={false}
                          disabled={!isEditable}
                          className="text-lg font-medium"
                          data-testid="counted-cash-input"
                        />
                        {calculations?.cashFloat > 0 && (
                          <p className="text-xs text-slate-400 mt-1">
                            Contar todo o dinheiro físico na caixa (inclui fundo)
                          </p>
                        )}
                      </div>
                      
                      <div>
                        <Label className="text-xs text-slate-500">Diferença</Label>
                        <DifferenceDisplay value={calculations?.cashDiff} />
                      </div>
                      
                      {calculations?.depositable !== null && (
                        <div className="pt-2 border-t">
                          <Label className="text-xs text-slate-500">Depositável</Label>
                          <p className="text-lg font-bold text-slate-900">
                            {formatCurrency(calculations?.depositable)}
                          </p>
                          {calculations?.cashFloat > 0 && (
                            <p className="text-xs text-slate-400">
                              ({formatCurrency(calculations?.countedCash)} - {formatCurrency(calculations?.cashFloat)} fundo)
                            </p>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  
                  {/* Card section */}
                  <Card className="border-blue-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-blue-600" />
                        Multibanco / TPA
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label className="text-xs text-slate-500">{calculations?.reclassified ? 'Esperado (corrigido)' : 'Esperado WinTouch'}</Label>
                        <p className="text-xl font-bold text-blue-600">{formatCurrency(calculations?.expectedCard)}</p>
                      </div>
                      
                      <div>
                        <Label htmlFor="tpa-closing">Fecho TPA</Label>
                        <CurrencyInput
                          id="tpa-closing"
                          value={tpaClosing}
                          onChange={setTpaClosing}
                          allowNegative={false}
                          disabled={!isEditable}
                          className="text-lg font-medium"
                          data-testid="tpa-closing-input"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-xs text-slate-500">Diferença</Label>
                        <DifferenceDisplay value={calculations?.cardDiff} />
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Notes */}
                <div>
                  <Label htmlFor="notes">Observações</Label>
                  <Textarea
                    id="notes"
                    placeholder="Notas adicionais sobre o fecho..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    disabled={!isEditable}
                    rows={3}
                    data-testid="notes-input"
                  />
                </div>
                
                {/* Confirmed snapshot info */}
                {isConfirmed && closure.confirmed_snapshot && (
                  <Alert className="bg-slate-50 border-slate-200">
                    <Lock className="h-4 w-4" />
                    <AlertTitle>Fecho Confirmado</AlertTitle>
                    <AlertDescription>
                      Confirmado em {closure.closed_at ? format(new Date(closure.closed_at), "dd/MM/yyyy 'às' HH:mm", { locale: pt }) : '—'}
                      {closure.closed_by && ` por ${closure.closed_by}`}
                    </AlertDescription>
                  </Alert>
                )}
                
                {/* Post-close data change warning */}
                {closure.has_data_changed_after_close && (
                  <Alert variant="destructive">
                    <XCircle className="h-4 w-4" />
                    <AlertTitle>Dados Alterados Após Fecho</AlertTitle>
                    <AlertDescription>
                      Foram importados novos documentos WinTouch para este dia após o fecho ter sido confirmado.
                      Os valores esperados atuais podem ser diferentes do snapshot confirmado.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            )}
            
            {/* Actions */}
            {closure.status !== 'NO_DATA' && (
              <CardFooter className="flex flex-wrap gap-2 justify-between border-t pt-4">
                <div className="flex gap-2">
                  {isEditable && (
                    <>
                      <Button 
                        variant="outline" 
                        onClick={handleSave}
                        disabled={saving}
                        data-testid="save-btn"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Guardar
                      </Button>
                      
                      <Button 
                        onClick={handleConfirm}
                        disabled={saving || !canConfirm}
                        data-testid="confirm-btn"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Confirmar Fecho
                      </Button>
                    </>
                  )}
                </div>
                
                {canReopen && (
                  <div>
                    {showReopen ? (
                      <div className="flex items-center gap-2">
                        <Input
                          placeholder="Motivo da reabertura..."
                          value={reopenReason}
                          onChange={(e) => setReopenReason(e.target.value)}
                          className="w-[200px]"
                          data-testid="reopen-reason-input"
                        />
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={handleReopen}
                          disabled={saving || !reopenReason.trim()}
                          data-testid="confirm-reopen-btn"
                        >
                          Confirmar
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => { setShowReopen(false); setReopenReason(''); }}
                        >
                          Cancelar
                        </Button>
                      </div>
                    ) : (
                      <Button 
                        variant="outline"
                        onClick={() => setShowReopen(true)}
                        data-testid="reopen-btn"
                      >
                        <Unlock className="w-4 h-4 mr-2" />
                        Reabrir Fecho
                      </Button>
                    )}
                  </div>
                )}
              </CardFooter>
            )}
          </Card>
        </div>
      )}
      </div>
    </div>
  );
}
